import { useState, useCallback, useEffect, createContext, useContext } from 'react';
import { useTheme } from '../context/ThemeContext';

interface ToastEntry {
  id: number;
  message: string;
}

interface ErrorToastContextType {
  showError: (message: string) => void;
}

const ErrorToastContext = createContext<ErrorToastContextType>({
  showError: () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export function useErrorToast() {
  return useContext(ErrorToastContext);
}

let nextId = 0;

export function ErrorToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);

  const showError = useCallback((message: string) => {
    const id = nextId++;
    setToasts((prev) => [...prev, { id, message }]);
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ErrorToastContext.Provider value={{ showError }}>
      {children}
      <ErrorToastContainer toasts={toasts} onDismiss={dismiss} />
    </ErrorToastContext.Provider>
  );
}

function ErrorToastContainer({
  toasts,
  onDismiss,
}: {
  toasts: ToastEntry[];
  onDismiss: (id: number) => void;
}) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {toasts.map((toast) => (
        <ErrorToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ErrorToastItem({
  toast,
  onDismiss,
}: {
  toast: ToastEntry;
  onDismiss: (id: number) => void;
}) {
  const { isDark } = useTheme();

  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss(toast.id);
    }, 5000);
    return () => clearTimeout(timer);
  }, [toast.id, onDismiss]);

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3 rounded-xl border shadow-lg animate-in slide-in-from-right-5 fade-in duration-200 ${
        isDark
          ? 'bg-[#1a1d26] border-red-500/30 text-gray-200'
          : 'bg-white border-red-200 text-gray-800'
      }`}
      role="alert"
    >
      <div
        className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
          isDark ? 'bg-red-500/20' : 'bg-red-100'
        }`}
      >
        <i
          className={`ri-error-warning-line text-xs ${
            isDark ? 'text-red-400' : 'text-red-600'
          }`}
        />
      </div>
      <p className="text-sm flex-1">{toast.message}</p>
      <button
        onClick={() => onDismiss(toast.id)}
        className={`flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
          isDark
            ? 'text-gray-400 hover:text-gray-300 hover:bg-white/10'
            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
        }`}
        aria-label="Dismiss error"
      >
        <i className="ri-close-line text-sm" />
      </button>
    </div>
  );
}
